import { createContext, useContext } from "react";

export type Language = "he" | "ru" | "ar";

export function getDirection(lang: Language): "rtl" | "ltr" {
  return lang === "ru" ? "ltr" : "rtl";
}

export const LANGUAGE_OPTIONS: { value: Language; label: string }[] = [
  { value: "he", label: "עברית" },
  { value: "ru", label: "Русский" },
  { value: "ar", label: "العربية" },
];

// ── Translation dictionary ─────────────────────────────────

const translations = {
  // ── Common ──
  back: { he: "חזור", ru: "Назад", ar: "رجوع" },
  backToPrevStep: { he: "לחזור לשלב הקודם", ru: "К предыдущему шагу", ar: "الرجوع للخطوة السابقة" },
  backToHome: { he: "חזור לדף הבית", ru: "На главную", ar: "الرئيسية" },
  continueToDesign: { he: "המשך לעיצוב", ru: "Далее — дизайн", ar: "التالي — التصميم" },
  continueToSummary: { he: "המשך לסיכום", ru: "К итогам", ar: "إلى الملخص" },
  continueToDetails: { he: "המשך להשארת פרטים", ru: "Оставить данные", ar: "المتابعة لترك التفاصيل" },
  summaryHeading: { he: "הצעת המחיר הראשונית שלכם", ru: "Ваше предварительное предложение", ar: "عرض السعر المبدئي" },
  summaryConversionHint: { he: "להמשך להצעה מסודרת, בדיקת פרטים ותיאום מהיר — השאירו פרטים בשלב הבא", ru: "Для точного расчёта оставьте данные на следующем шаге", ar: "للحصول على عرض دقيق، اتركوا تفاصيلكم في الخطوة التالية" },
  summaryTrustLine: { he: "ללא התחייבות • נחזור אליכם עם הצעה מסודרת", ru: "Без обязательств • Мы свяжемся с вами", ar: "بدون التزام • سنعود إليكم بعرض منظم" },
  backToVisualization: { he: "חזור להדמיה", ru: "Назад к визуализации", ar: "العودة للمعاينة" },
  backToSummary: { he: "חזור לסיכום", ru: "Назад к итогам", ar: "العودة للملخص" },
  home: { he: "דף הבית", ru: "Главная", ar: "الرئيسية" },
  save: { he: "שמור", ru: "Сохранить", ar: "حفظ" },
  send: { he: "שלח", ru: "Отправить", ar: "إرسال" },
  cancel: { he: "ביטול", ru: "Отмена", ar: "إلغاء" },
  delete: { he: "מחק", ru: "Удалить", ar: "حذف" },
  required: { he: "חובה", ru: "Обязательно", ar: "مطلوب" },
  optional: { he: "אופציונלי", ru: "По желанию", ar: "اختياري" },
  sqm: { he: "מ״ר", ru: "м²", ar: "م²" },
  meters: { he: "מ׳", ru: "м", ar: "م" },
  metersUnit: { he: "מ'", ru: "м", ar: "م" },
  units: { he: "יח׳", ru: "шт.", ar: "قطعة" },

  // ── Stepper ──
  stepMeasurements: { he: "מידות", ru: "Замеры", ar: "القياسات" },
  stepCustomization: { he: "הדמיה", ru: "Визуализация", ar: "المعاينة" },
  stepSummary: { he: "סיכום מחיר", ru: "Смета", ar: "ملخص السعر" },
  stepDetails: { he: "פרטים ואישור", ru: "Оформление", ar: "التفاصيل والتأكيد" },
  stepperAriaLabel: { he: "שלבי המחשבון", ru: "Шаги калькулятора", ar: "خطوات الحاسبة" },

  // ── Index / Landing ──
  landingBadge: { he: "מחשבון תקרות מתוחה", ru: "Калькулятор натяжных потолков", ar: "حاسبة الأسقف المشدودة" },
  landingH1: { he: "בדקו מחיר לתקרה מתוחה תוך פחות מדקה", ru: "Узнайте стоимость натяжного потолка менее чем за минуту", ar: "اكتشف سعر السقف المشدود في أقل من دقيقة" },
  landingSubtitle: { he: "קבלו הערכת מחיר ראשונית אונליין, במהירות וללא התחייבות", ru: "Получите предварительную оценку онлайн — быстро и без обязательств", ar: "احصل على تقدير مبدئي أونلاين، بسرعة وبدون التزام" },
  hasDimensions: { he: "חישוב לפי מידות", ru: "Расчёт по размерам", ar: "حساب حسب القياسات" },
  noDimensions: { he: "חישוב לפי גודל משוער", ru: "Расчёт по примерному размеру", ar: "حساب حسب حجم تقريبي" },
  hasDimensionsDesc: { he: "לחישוב מדויק יותר לפי שטח קיים", ru: "Точный расчёт по известной площади", ar: "حساب دقيق حسب المساحة المعروفة" },
  noDimensionsDesc: { he: "להערכת מחיר מהירה גם בלי מידות מדויקות", ru: "Быстрая оценка без точных замеров", ar: "تقدير سريع حتى بدون قياسات دقيقة" },
  start: { he: "התחל", ru: "Начать", ar: "ابدأ" },
  startCalcCta: { he: "התחל חישוב מחיר", ru: "Рассчитать стоимость", ar: "ابدأ حساب السعر" },
  ctaReassurance: { he: "מתאים גם אם אין לכם מידות מדויקות", ru: "Подходит даже без точных замеров", ar: "مناسب حتى بدون قياسات دقيقة" },
  trustNoDust: { he: "ללא אבק", ru: "Без пыли", ar: "بدون غبار" },
  trustFastInstall: { he: "התקנה מהירה", ru: "Быстрая установка", ar: "تركيب سريع" },
  trustWarranty10: { he: "אחריות ל-10 שנים", ru: "Гарантия 10 лет", ar: "ضمان 10 سنوات" },
  trustNationwide: { he: "שירות בכל הארץ", ru: "По всей стране", ar: "خدمة في كل البلاد" },
  credSince2017: { he: "מ-2017", ru: "С 2017", ar: "منذ 2017" },
  credOver1000: { he: "מעל 1,000 התקנות", ru: "Более 1 000 установок", ar: "أكثر من 1,000 تركيب" },
  howItWorksTitle: { he: "איך זה עובד", ru: "Как это работает", ar: "كيف يعمل" },
  howStep1: { he: "בוחרים סוג חדר", ru: "Выбираете тип комнаты", ar: "تختار نوع الغرفة" },
  howStep2: { he: "מקבלים הערכת מחיר ראשונית", ru: "Получаете предварительную оценку", ar: "تحصل على تقدير مبدئي" },
  howStep3: { he: "משאירים פרטים להצעה מדויקת והמשך מהיר", ru: "Оставляете данные для точного расчёта", ar: "تترك بياناتك لعرض دقيق ومتابعة سريعة" },
  whyStartHereTitle: { he: "למה להתחיל כאן", ru: "Почему стоит начать здесь", ar: "لماذا تبدأ من هنا" },
  whyStartHereBody: {
    he: "המחיר שמתקבל במחשבון הוא הערכה ראשונית בלבד, לפי סוג החדר, גודל משוער, גימור ותוספות נבחרות.\nלקבלת הצעת מחיר מדויקת יותר ניתן להשאיר פרטים בסיום התהליך.",
    ru: "Цена в калькуляторе — лишь предварительная оценка по типу комнаты, площади, покрытию и допам.\nДля точного расчёта оставьте данные в конце.",
    ar: "السعر في الحاسبة هو تقدير مبدئي فقط حسب نوع الغرفة والمساحة والتشطيب والإضافات.\nللحصول على عرض دقيق، اترك بياناتك في نهاية العملية.",
  },
  phoneCtaTitle: { he: "יש לכם שאלה לפני שמתחילים?", ru: "Есть вопрос перед началом?", ar: "عندك سؤال قبل البدء؟" },
  phoneCtaBody: { he: "חייגו אלינו עכשיו לשיחה מהירה", ru: "Позвоните нам для быстрой консультации", ar: "اتصلوا بنا الآن لمكالمة سريعة" },
  callNow: { he: "חייגו עכשיו", ru: "Позвонить", ar: "اتصل الآن" },
  trustFast: { he: "הערכת מחיר מהירה", ru: "Быстрый расчёт", ar: "حساب سريع" },
  trustNoMeasure: { he: "מתאים גם בלי מידות", ru: "Можно и без замеров", ar: "يعمل أيضاً بدون قياسات" },
  trustOptions: { he: "אפשרויות גימור ותאורה", ru: "Покрытие и освещение на выбор", ar: "خيارات تشطيب وإضاءة" },
  loginHint: { he: "יש לך חישובים שמורים? התחבר", ru: "Есть сохранённые расчёты? Войти", ar: "عندك حسابات محفوظة؟ سجّل دخول" },
  personalArea: { he: "כניסה לאזור האישי", ru: "Личный кабинет", ar: "حسابي" },
  adminPricing: { he: "ניהול מחירון", ru: "Управление ценами", ar: "إدارة الأسعار" },

  // ── Landing SEO paragraph ──
  landingSupportParagraph: {
    he: "מחשבון זה מיועד לקבלת הערכת מחיר ראשונית לתקרה מתוחה ולהנמכת תקרה לפי נתוני החדר, סוג הגימור, תאורה ותוספות אפשריות. ניתן להתחיל גם בלי מידות מדויקות ולקבל סדר גודל מהיר. תקרה מתוחה בגימור מט מתאימה גם למי שמחפש מראה חלק ונקי בסגנון גבס.",
    ru: "Калькулятор поможет прикинуть стоимость натяжного потолка с учётом размера комнаты, покрытия, освещения и дополнений. Можно начать даже без точных замеров. Матовый натяжной потолок — отличная альтернатива гипсокартону: ровный и аккуратный.",
    ar: "تساعدك هذه الحاسبة في تقدير تكلفة السقف المشدود حسب مساحة الغرفة ونوع التشطيب والإضاءة والإضافات. يمكنك البدء حتى بدون قياسات دقيقة. السقف المشدود بتشطيب مطفي هو بديل ممتاز للجبس — ناعم ومرتب.",
  },

  // ── Landing meta ──
  metaTitle: {
    he: "מחשבון מחיר לתקרה מתוחה והנמכת תקרה | הערכת מחיר מהירה",
    ru: "Калькулятор натяжного потолка — быстрый расчёт стоимости",
    ar: "حاسبة السقف المشدود — تقدير سريع للسعر",
  },
  metaDescription: {
    he: "מחשבון מחיר לתקרה מתוחה ולהנמכת תקרה. קבלו הערכת מחיר ראשונית לפי גודל חדר, גימור, תאורה ותוספות. מתאים גם למי שמחפש מראה גבס.",
    ru: "Рассчитайте примерную стоимость натяжного потолка: размер комнаты, покрытие, освещение, допы. Подходит и для матового потолка под гипсокартон.",
    ar: "احسب تكلفة السقف المشدود: مساحة الغرفة، التشطيب، الإضاءة، والإضافات. مناسب أيضاً لمن يريد مظهر الجبس.",
  },

  // ── FAQ ──
  faqTitle: { he: "שאלות נפוצות", ru: "Вопросы и ответы", ar: "أسئلة شائعة" },
  faqQ1: { he: "האם המחיר שמופיע במחשבון הוא סופי?", ru: "Цена в калькуляторе окончательная?", ar: "هل السعر في الحاسبة نهائي؟" },
  faqA1: { he: "לא. המחשבון נותן הערכת מחיר ראשונית בלבד. המחיר הסופי נקבע לפי מדידה מדויקת ותנאי השטח.", ru: "Нет, это предварительная оценка. Точная цена определяется после замера на месте.", ar: "لا، هذا تقدير مبدئي فقط. السعر النهائي يُحدد بعد القياس في الموقع." },
  faqQ2: { he: "האם המחשבון מתאים גם להנמכת תקרה?", ru: "Подходит ли для понижения потолка?", ar: "هل يصلح لخفض السقف أيضاً؟" },
  faqA2: { he: "כן. תקרה מתוחה היא פתרון יעיל גם להנמכת תקרה, כולל שילוב תאורה ושיפור מראה החלל.", ru: "Да, натяжной потолок — удобное решение и для понижения: с подсветкой и красивым видом.", ar: "نعم، السقف المشدود حل عملي لخفض السقف أيضاً مع إمكانية دمج الإضاءة." },
  faqQ3: { he: "האם אפשר לקבל מראה של תקרת גבס?", ru: "Можно сделать вид как гипсокартон?", ar: "هل يمكن الحصول على شكل يشبه الجبس؟" },
  faqA3: { he: "כן. גימור מט מאפשר מראה חלק, אחיד ונקי שמתאים למי שמחפש סגנון דומה לגבס.", ru: "Да, матовое покрытие даёт ровный и чистый вид — как гипсокартон.", ar: "نعم، التشطيب المطفي يعطي سطحاً ناعماً ونظيفاً مثل الجبس." },
  faqQ4: { he: "האם אפשר לחשב גם בלי מידות?", ru: "Можно посчитать без замеров?", ar: "هل يمكن الحساب بدون قياسات؟" },
  faqA4: { he: "כן. אפשר להתחיל גם בלי מידות ולקבל הערכת מחיר כללית לפי גודל משוער של החדר.", ru: "Да, просто выберите тип комнаты и примерный размер — получите ориентировочную стоимость.", ar: "نعم، فقط اختر نوع الغرفة والحجم التقريبي واحصل على تقدير." },

  // ── Entry ──
  howToCalculate: { he: "איך נחשב את המחיר?", ru: "Как рассчитаем стоимость?", ar: "كيف نحسب السعر؟" },
  selectPath: { he: "בחרו את שיטת החישוב המתאימה לכם", ru: "Выберите подходящий способ расчёта", ar: "اختاروا طريقة الحساب المناسبة لكם" },
  canChangeLater: { he: "אפשר לשנות בהמשך אם צריך", ru: "Можно изменить позже", ar: "يمكن التغيير لاحقاً إذا لزم الأمر" },

  // ── Measurements ──
  howToEnterData: { he: "איך תרצה להזין נתונים?", ru: "Как ввести данные?", ar: "كيف تريد إدخال البيانات؟" },
  selectPathByDimOrRoom: { he: "בחר מסלול חישוב לפי מידות או לפי חדר סטנדרטי.", ru: "По размерам или по типу комнаты.", ar: "حسب القياسات أو حسب نوع الغرفة." },
  noDimensionsCalcByRoom: { he: "אין לי מידות, חשב לפי חדר סטנדרטי", ru: "Нет размеров — считаем по комнате", ar: "بدون قياسات — احسب حسب الغرفة" },
  selectSpaceType: { he: "בחר סוג חלל", ru: "Выберите комнату", ar: "اختر نوع الغرفة" },
  whichRoom: { he: "באיזה חדר תרצה להתקין תקרה מתוחה?", ru: "В какую комнату нужен потолок?", ar: "في أي غرفة تريد تركيب السقف؟" },
  enterDimensions: { he: "הזן מידות", ru: "Введите размеры", ar: "أدخل القياسات" },
  enterDimensionsDesc: { he: "הזן את מידות החדר לחישוב מדויק", ru: "Укажите размеры комнаты для точного расчёта", ar: "أدخل أبعاد الغرفة للحساب الدقيق" },
  length: { he: "אורך (מ׳)", ru: "Длина (м)", ar: "الطول (م)" },
  width: { he: "רוחב (מ׳)", ru: "Ширина (м)", ar: "العرض (م)" },
  area: { he: "שטח", ru: "Площадь", ar: "المساحة" },
  perimeter: { he: "היקף", ru: "Периметр", ar: "المحيط" },
  totalAreaOnly: { he: "יש לי שטח כולל בלבד", ru: "Знаю только площадь", ar: "أعرف المساحة فقط" },
  totalAreaSqm: { he: "שטח כולל במ״ר", ru: "Площадь (м²)", ar: "المساحة الإجمالية (م²)" },
  enterLengthWidth: { he: "הזן אורך ורוחב במקום", ru: "Ввести длину и ширину", ar: "أدخل الطول والعرض" },
  minimumPriceWarning: { he: "ייתכן ויחול מחיר מינימום עבודה", ru: "Может применяться минимальная стоимость", ar: "قد يُطبَّق حد أدنى للسعر" },
  ceilingHeight: { he: "גובה תקרה קיים (מ׳)", ru: "Высота потолка (м)", ar: "ارتفاع السقف (م)" },
  defaultHeight: { he: "ברירת מחדל", ru: "По умолчанию", ar: "افتراضي" },
  structureAndConditions: { he: "תנאים מיוחדים", ru: "Особые условия", ar: "ظروف خاصة" },
  markConditions: { he: "סמנו אם רלוונטי", ru: "Отметьте, если есть особенности", ar: "حدّد إذا كانت هناك ظروف خاصة" },
  nonStandardRoom: { he: "חדר לא סטנדרטי (מעל 4 פינות)", ru: "Нестандартная комната (больше 4 углов)", ar: "غرفة غير قياسية (أكثر من 4 زوايا)" },
  nonStandardTooltip: { he: "בחר אם לחדר יש יותר מ־4 פינות (צורה מורכבת).", ru: "Если у комнаты сложная форма — больше 4 углов.", ar: "اختر إذا كانت الغرفة بشكل معقد — أكثر من 4 زوايا." },
  cornerCount: { he: "מספר פינות", ru: "Кол-во углов", ar: "عدد الزوايا" },
  dismantleExistingCeiling: { he: "פירוק תקרה קיימת", ru: "Демонтаж старого потолка", ar: "إزالة السقف القديم" },
  ventOpening: { he: "פתח ונטה", ru: "Вент. отверстие", ar: "فتحة تهوية" },
  aboveOpenPool: { he: "תקרה מעל בריכה פתוחה", ru: "Потолок над открытым бассейном", ar: "سقف فوق مسبح مفتوح" },
  ventilationSystem: { he: "מערכת אוורור קיימת", ru: "Вентиляция в комнате", ar: "يوجد نظام تهوية" },
  complexAccess: { he: "גישה מורכבת", ru: "Сложный доступ", ar: "وصول صعب" },
  complexAccessTooltip: { he: "למשל: קומות גבוהות, פיגומים, גישה מוגבלת", ru: "Высокие этажи, леса, ограниченный подход", ar: "طوابق عالية، سقالات، وصول محدود" },
  dismantleLightPoints: { he: "פירוק נקודות תאורה ישנות", ru: "Демонтаж старых светильников", ar: "إزالة نقاط الإضاءة القديمة" },
  dismantleLightPointsTooltip: { he: "כמה נקודות תאורה קיימות צריך לפרק לפני התקנה.", ru: "Сколько старых точек нужно снять перед монтажом.", ar: "كم نقطة إضاءة قديمة يجب إزالتها قبل التركيب." },
  lightPointsQuantity: { he: "כמות נקודות לפירוק", ru: "Точек к демонтажу", ar: "عدد النقاط للإزالة" },
  calculatedArea: { he: "שטח מחושב", ru: "Расчётная площадь", ar: "المساحة المحسوبة" },
  estimatedAreaForCalc: { he: "שטח משוער לחישוב", ru: "Примерная площадь", ar: "المساحة التقريبية" },
  enterDimensionsToCalc: { he: "הזן מידות כדי לחשב", ru: "Введите размеры для расчёта", ar: "أدخل القياسات للحساب" },
  selectRoomAndSize: { he: "בחר חדר וגודל לקבלת שטח משוער", ru: "Выберите комнату и размер", ar: "اختر غرفة وحجماً" },

  // ── No-dimensions path ──
  selectStandardRoom: { he: "בחרו סוג חדר וגודל משוער", ru: "Выберите тип комнаты и размер", ar: "اختاروا نوع الغرفة والحجم" },
  selectRoomAndSizeDesc: { he: "בחרו את סוג החדר והגודל המשוער כדי לקבל הערכת מחיר ראשונית", ru: "Выберите тип комнаты и примерный размер — посчитаем без замеров.", ar: "اختر نوع الغرفة والحجم التقريبي — نحسب بدون قياسات." },
  roomType: { he: "סוג חדר", ru: "Комната", ar: "نوع الغرفة" },
  estimatedSize: { he: "גודל משוער", ru: "Размер", ar: "الحجم التقريبي" },
  estimatedAreaResult: { he: "שטח משוער לחישוב", ru: "Примерная площадь", ar: "المساحة التقريبية" },
  estimatedAreaNote: { he: "החישוב מבוסס על שטח משוער לפי סוג חדר", ru: "Расчёт по примерной площади для этого типа комнаты", ar: "الحساب مبني على مساحة تقريبية لهذا النوع من الغرف" },
  finalPriceNote: { he: "המחיר הסופי ייקבע לפי מידות בפועל / תמונות / מדידה", ru: "Точная цена — после замера на месте", ar: "السعر النهائي — بعد القياس في الموقع" },

  // ── Space types ──
  spaceSalon: { he: "סלון", ru: "Гостиная", ar: "صالون" },
  spaceBedroom: { he: "חדר שינה", ru: "Спальня", ar: "غرفة نوم" },
  spaceKitchen: { he: "מטבח", ru: "Кухня", ar: "مطبخ" },
  spaceHallway: { he: "מסדרון", ru: "Коридор", ar: "ممر" },
  spaceOffice: { he: "משרד", ru: "Кабинет", ar: "مكتب" },
  spaceShower: { he: "מקלחת", ru: "Ванная", ar: "حمّام" },
  spaceToilet: { he: "שירותים", ru: "Туалет", ar: "مرحاض" },
  spaceMikvah: { he: "מקווה", ru: "Миква", ar: "مِكفه" },

  // ── Estimated room types ──
  roomSalon: { he: "סלון", ru: "Гостиная", ar: "صالون" },
  roomBedroom: { he: "חדר שינה", ru: "Спальня", ar: "غرفة نوم" },
  roomKidsRoom: { he: "חדר ילדים", ru: "Детская", ar: "غرفة أطفال" },
  roomKitchen: { he: "מטבח", ru: "Кухня", ar: "مطبخ" },
  roomBathroom: { he: "מקלחת", ru: "Ванная", ar: "حمّام" },
  roomHallway: { he: "מסדרון", ru: "Коридор", ar: "ممر" },
  roomOffice: { he: "משרד", ru: "Кабинет", ar: "مكتب" },
  roomMikvah: { he: "מקווה", ru: "Миква", ar: "مِكفه" },

  // ── Size keys ──
  sizeSmall: { he: "קטן", ru: "Маленький", ar: "صغير" },
  sizeMedium: { he: "בינוני", ru: "Средний", ar: "متوسط" },
  sizeLarge: { he: "גדול", ru: "Большой", ar: "كبير" },

  // ── Customization ──
  ceilingType: { he: "סוג תקרה", ru: "Тип потолка", ar: "نوع السقف" },
  finishGlossy: { he: "מבריק", ru: "Глянец", ar: "لامع" },
  finishGlossyDesc: { he: "משטח משתקף", ru: "Зеркальная поверхность", ar: "سطح عاكس" },
  finishMatte: { he: "מט", ru: "Мат", ar: "مطفي" },
  finishMatteDesc: { he: "כמו גבס חלק", ru: "Как ровный гипсокартон", ar: "مثل الجبس الناعم" },
  finishSatin: { he: "סאטן", ru: "Сатин", ar: "ساتان" },
  finishSatinDesc: { he: "מראה צבע שמן", ru: "Эффект масляной краски", ar: "مظهر طلاء زيتي" },
  finishPrint: { he: "הדפס", ru: "Печать", ar: "طباعة" },
  finishPrintDesc: { he: "הדפס בלבד", ru: "Только печать", ar: "طباعة فقط" },
  finishBacklit: { he: "תקרה מוארת", ru: "Световой потолок", ar: "سقف مضيء" },
  finishBacklitDesc: { he: "כל שטח התקרה מואר", ru: "Вся поверхность светится", ar: "كامل السقف مضاء" },
  finishBacklitPrint: { he: "תקרה מוארת הדפס", ru: "Световой с печатью", ar: "سقف مضيء مع طباعة" },
  finishBacklitPrintDesc: { he: "תקרה מוארת עם הדפס", ru: "Световой потолок с рисунком", ar: "سقف مضيء مع رسمة" },
  printInfo: { he: "תמונה להדפסה ניתן לבחור באתר Shutterstock", ru: "Изображение для печати можно выбрать на Shutterstock", ar: "يمكن اختيار صورة للطباعة من موقع Shutterstock" },
  selectColor: { he: "בחר צבע", ru: "Выберите цвет", ar: "اختر اللون" },

  // ── Time selector ──
  timeDay: { he: "יום", ru: "День", ar: "نهار" },
  timeNight: { he: "לילה", ru: "Ночь", ar: "ليل" },

  // ── Lighting & Accessories ──
  lightingAndAccessories: { he: "תאורה ואביזרים", ru: "Свет и аксессуары", ar: "الإضاءة والإكسسوارات" },
  lightingAccordionLabel: { he: "תאורה", ru: "Освещение", ar: "الإضاءة" },
  extrasAccordionLabel: { he: "תוספות", ru: "Дополнения", ar: "إضافات" },
  optionalLabel: { he: "אופציונלי", ru: "необязательно", ar: "اختياري" },
  advancedFinishes: { he: "גימורים מתקדמים", ru: "Продвинутые покрытия", ar: "تشطيبات متقدمة" },
  customizationHelper: { he: "בחרו סגנון בסיסי לתקרה. אפשר להוסיף תאורה ותוספות בהמשך.", ru: "Выберите базовый стиль потолка. Освещение и дополнения можно добавить позже.", ar: "اختاروا نمطاً أساسياً للسقف. يمكن إضافة إضاءة وإضافات لاحقاً." },
  lightingNotAvailable: { he: "אביזרי תאורה אינם זמינים עם תקרה מוארת", ru: "Со световым потолком доп. свет недоступен", ar: "ملحقات الإضاءة غير متوفرة مع السقف المضيء" },
  noExtrasSelected: { he: "ללא תוספות", ru: "Без дополнений", ar: "بدون إضافات" },

  accSpotlights: { he: "ספוטים שקועים", ru: "Встроенные споты", ar: "سبوتات مدمجة" },
  accSpotlightsDesc: { he: "גופי תאורה שקועים בתקרה", ru: "Точечные светильники в потолке", ar: "وحدات إضاءة مدمجة في السقف" },
  accSpotlightsSub: { he: "תושבת בלבד", ru: "Только основание", ar: "القاعدة فقط" },

  accRecessedLed: { he: "פס לד שקוע", ru: "Встроенная LED-лента", ar: "شريط LED مدمج" },
  accRecessedLedDesc: { he: "רצועת לד משולבת בתקרה", ru: "Светодиодная лента внутри потолка", ar: "شريط LED داخل السقف" },
  accRecessedLedSub: { he: "כולל פרופיל, לד, שנאי והתקנה", ru: "Профиль + LED + блок питания + монтаж", ar: "يشمل البروفيل والإضاءة والمحول والتركيب" },

  accPerimeterLight: { he: "תאורה היקפית", ru: "Свет по периметру", ar: "إضاءة محيطية" },
  accPerimeterLightDesc: { he: "פס אור לאורך היקף התקרה", ru: "Световая линия вдоль периметра", ar: "خط ضوء على طول محيط السقف" },
  accPerimeterLightSub: { he: "כולל פרופיל מותאם, לד, שנאי והתקנה", ru: "Профиль + LED + блок питания + монтаж", ar: "يشمل البروفيل والإضاءة والمحول والتركيب" },
  perimeterFullSurround: { he: "תוספת תאורה היקפית לכל ההיקף", ru: "Свет по всему периметру", ar: "إضاءة على كامل المحيط" },

  accCurtainLight: { he: "תאורת וילון", ru: "Подсветка карниза", ar: "إضاءة الستارة" },
  accCurtainLightDesc: { he: "פס אור מוסתר מאחורי ארגז הוילון", ru: "Скрытый свет за карнизом", ar: "إضاءة مخفية خلف صندوق الستارة" },
  accCurtainLightSub: { he: "כולל פרופיל, לד, שנאי והתקנה", ru: "Профиль + LED + блок питания + монтаж", ar: "يشمل البروفيل والإضاءة والمحول والتركيب" },

  accSurfaceMount: { he: "תאורה צמודת תקרה", ru: "Накладной светильник", ar: "إضاءة سطحية" },
  accSurfaceMountDesc: { he: "גוף תאורה על פני התקרה", ru: "Светильник на поверхности потолка", ar: "وحدة إضاءة على سطح السقف" },
  accSurfaceMountSub: { he: "תושבת בלבד", ru: "Только основание", ar: "القاعدة فقط" },

  accMagneticTrack: { he: "פס מגנטי שקוע", ru: "Магнитный трек", ar: "مسار مغناطيسي مدمج" },
  accMagneticTrackDesc: { he: "מסלול מגנטי לגופי תאורה", ru: "Магнитная шина для светильников", ar: "مسار مغناطيسي لوحدات الإضاءة" },
  accMagneticTrackSub: { he: "כולל פרופיל, שנאי והתקנה", ru: "Профиль + блок питания + монтаж", ar: "يشمل البروفيل والمحول والتركيب" },
  trackLength: { he: "אורך פס", ru: "Длина трека", ar: "طول المسار" },
  headCount: { he: "מספר גופים", ru: "Кол-во светильников", ar: "عدد الوحدات" },

  accPendantLight: { he: "תאורה תלויה", ru: "Подвесной светильник", ar: "إضاءة معلّقة" },
  accPendantLightDesc: { he: "גוף תאורה תלוי מהתקרה", ru: "Светильник на подвесе", ar: "وحدة إضاءة معلّقة من السقف" },
  accPendantLightSub: { he: "תושבת בלבד", ru: "Только основание", ar: "القاعدة فقط" },

  lightingGeneralNote: { he: "גופי התאורה בתמונה להמחשה בלבד, אלא אם צוין אחרת.", ru: "Светильники на фото — для примера, если не указано иное.", ar: "الإضاءة في الصورة للتوضيح فقط، ما لم يُذكر غير ذلك." },

  quantity: { he: "כמות", ru: "Кол-во", ar: "الكمية" },
  lengthLabel: { he: "אורך", ru: "Длина", ar: "الطول" },

  // ── Kelvin ──
  lightingTone: { he: "גוון תאורה", ru: "Тон света", ar: "درجة الإضاءة" },
  kelvinWarm: { he: "חם", ru: "Тёплый", ar: "دافئ" },
  kelvinNeutral: { he: "ניטרלי", ru: "Нейтральный", ar: "محايد" },
  kelvinCool: { he: "קר", ru: "Холодный", ar: "بارد" },

  // ── Extras ──
  extras: { he: "תוספות", ru: "Допы", ar: "إضافات" },
  thermalInsulation: { he: "בידוד תרמי", ru: "Теплоизоляция", ar: "عزل حراري" },
  thermalInsulationDesc: { he: "שכבת בידוד מעל התקרה", ru: "Утеплитель над потолком", ar: "طبقة عزل فوق السقف" },
  acousticInsulation: { he: "בידוד אקוסטי", ru: "Шумоизоляция", ar: "عزل صوتي" },
  acousticInsulationDesc: { he: "מפחית רעשים מהקומה מעל", ru: "Уменьшает шум от соседей сверху", ar: "يقلل الضوضاء من الطابق العلوي" },
  serviceHatch: { he: "פתח שירות 60×60", ru: "Люк 60×60", ar: "فتحة خدمة 60×60" },
  serviceHatchDesc: { he: "גישה לצנרת ומערכות", ru: "Доступ к трубам и коммуникациям", ar: "وصول للأنابيب والتمديدات" },
  hiddenCurtain: { he: "נישה עבור וילון נסתר", ru: "Ниша под скрытый карниз", ar: "تجويف لستارة مخفية" },
  hiddenCurtainDesc: { he: "פרופיל מוסתר לתליית וילונות", ru: "Скрытый карниз для штор", ar: "بروفيل مخفي للستائر" },
  gypsumBulkhead: { he: "סינר גבס היקפי", ru: "Гипсовый короб по периметру", ar: "إطار جبس محيطي" },
  gypsumBulkheadDesc: { he: "תוספת בתשלום", ru: "За доп. плату", ar: "بتكلفة إضافية" },
  gypsumBulkheadInfo: { he: "היקף חדר", ru: "По периметру комнаты", ar: "محيط الغرفة" },
  gypsumBulkheadPricing: { he: "התמחור לפי מ׳ רץ", ru: "Цена за погонный метр", ar: "التسعير بالمتر الطولي" },
  existingGypsumBulkhead: { he: "סינר גבס קיים", ru: "Гипсовый короб уже есть", ar: "يوجد إطار جبس" },
  existingGypsumDesc: { he: "סינר גבס קיים בחלל – ללא תוספת מחיר", ru: "Короб уже установлен — без доплаты", ar: "إطار الجبس موجود — بدون تكلفة إضافية" },
  existingGypsumInfo: { he: "משפיע על התצוגה בלבד, ללא שינוי במחיר", ru: "Влияет только на вид, цена не меняется", ar: "يؤثر على المظهر فقط، بدون تغيير في السعر" },
  customAddons: { he: "תוספות מותאמות אישית", ru: "Свои позиции", ar: "إضافات مخصصة" },

  // ── Preview disclaimer ──
  previewDisclaimer: { he: "* התמונה להמחשה בלבד ואינה משקפת בהכרח את הכמות, המיקום או הפריסה המדויקת של האביזרים. החישוב הסופי מבוסס אך ורק על הפריטים והכמויות המופיעים ברשימת החישוב/בסיכום.", ru: "* Картинка — для наглядности. Количество, расположение и раскладка аксессуаров могут отличаться. Итоговый расчёт основан только на позициях в смете.", ar: "* الصورة توضيحية فقط. الكمية والموقع والتوزيع قد يختلفون. الحساب النهائي يعتمد فقط على بنود الملخص." },
  colorDisclaimer: { he: "הגוון בהדמיה להמחשה בלבד", ru: "Цвет на картинке приблизительный", ar: "اللون في الصورة تقريبي" },
  previewLoading: { he: "טוען תצוגה…", ru: "Загрузка…", ar: "جارٍ التحميل…" },
  previewUnavailable: { he: "תצוגה מקדימה לא זמינה עבור חדר זה", ru: "Просмотр для этой комнаты недоступен", ar: "المعاينة غير متوفرة لهذه الغرفة" },
  previewError: { he: "לא ניתן לטעון את התמונה", ru: "Не удалось загрузить фото", ar: "تعذّر تحميل الصورة" },

  // ── Price panel ──
  estimatedPrice: { he: "מחיר משוער", ru: "Примерная цена", ar: "السعر التقديري" },
  inclVatAndInstall: { he: "כולל מע״מ והתקנה מלאה", ru: "С НДС, включая монтаж", ar: "شامل الضريبة والتركيب الكامل" },
  exclVatInclInstall: { he: "כולל מיסים, ייצור והתקנה", ru: "Включает налоги, изготовление и установку", ar: "يشمل الضرائب، التصنيع والتركيب" },
  totalExclVat: { he: "כולל מיסים, ייצור והתקנה", ru: "Включает налоги, изготовление и установку", ar: "يشمل الضرائب، التصنيع والتركيب" },
  beforeVat: { he: "לפני מע״מ", ru: "Без НДС", ar: "قبل الضريبة" },
  vat: { he: "מע״מ", ru: "НДС", ar: "ضريبة" },
  total: { he: "סה״כ", ru: "Итого", ar: "الإجمالي" },
  totalInclVat: { he: "סה״כ כולל מע״מ", ru: "Итого с НДС", ar: "الإجمالي مع الضريبة" },
  moreItems: { he: "ועוד", ru: "и ещё", ar: "وأيضاً" },
  items: { he: "פריטים...", ru: "позиций…", ar: "عناصر…" },
  customAddonsLabel: { he: "תוספות מותאמות", ru: "Свои позиции", ar: "إضافات مخصصة" },
  minimumJobApplied: { he: "* יושם מחיר מינימום עבודה", ru: "* Применён минимальный заказ", ar: "* تم تطبيق الحد الأدنى للطلب" },
  priceDisclaimer: { he: "* החישוב מבוסס על הפריטים והכמויות בסיכום.", ru: "* Расчёт по позициям и количествам в смете.", ar: "* الحساب مبني على البنود والكميات في الملخص." },
  priceBreakdown: { he: "פירוט מחיר", ru: "Детали цены", ar: "تفاصيل السعر" },

  // ── Estimate page ──
  missingData: { he: "נתונים חסרים – חזור לשלב הקודם", ru: "Данных не хватает — вернитесь назад", ar: "بيانات ناقصة — ارجع للخطوة السابقة" },
  quoteTitle: { he: "הצעת מחיר", ru: "Смета", ar: "عرض سعر" },
  quoteNumber: { he: "מספר", ru: "№", ar: "رقم" },
  date: { he: "תאריך", ru: "Дата", ar: "التاريخ" },
  validUntil: { he: "תוקף", ru: "Действует до", ar: "صالح حتى" },
  totalToPay: { he: "סה״כ לתשלום", ru: "К оплате", ar: "المبلغ للدفع" },
  projectDetails: { he: "פרטי הפרויקט", ru: "Детали проекта", ar: "تفاصيل المشروع" },
  spaceTypeLabel: { he: "סוג חלל", ru: "Комната", ar: "نوع الغرفة" },
  areaLabel: { he: "שטח", ru: "Площадь", ar: "المساحة" },
  heightLabel: { he: "גובה", ru: "Высота", ar: "الارتفاع" },
  finishLabel: { he: "גימור", ru: "Покрытие", ar: "التشطيب" },
  colorLabel: { he: "צבע", ru: "Цвет", ar: "اللون" },
  lightingToneLabel: { he: "גוון תאורה", ru: "Тон света", ar: "درجة الإضاءة" },
  priceEstimatedDisclaimer: { he: "המחיר משוער עד מדידה/תמונות, המחיר הסופי נקבע בשטח", ru: "Цена ориентировочная — точная после замера на месте", ar: "السعر تقديري — النهائي بعد القياس في الموقع" },
  breakdownTitle: { he: "פירוט חישוב", ru: "Детализация", ar: "تفاصيل الحساب" },
  itemColumn: { he: "פריט", ru: "Позиция", ar: "البند" },
  quantityColumn: { he: "כמות", ru: "Кол-во", ar: "الكمية" },
  amountColumn: { he: "סכום", ru: "Сумма", ar: "المبلغ" },
  subtotalBeforeVat: { he: "סכום לפני מע״מ", ru: "Итого без НДС", ar: "المبلغ قبل الضريبة" },
  whatsIncluded: { he: "מה כלול במחיר", ru: "Что входит в цену", ar: "ما يشمله السعر" },
  whatsNotIncluded: { he: "מה לא כלול", ru: "Не входит в цену", ar: "ما لا يشمله السعر" },
  includedPvc: { he: "חומר PVC איכותי", ru: "Качественный ПВХ", ar: "مادة PVC عالية الجودة" },
  includedProfiles: { he: "פרופילים ואביזרים", ru: "Профили и крепёж", ar: "بروفيلات وإكسسوارات" },
  includedInstallation: { he: "התקנה מלאה מקצועית", ru: "Полный монтаж", ar: "تركيب كامل واحترافي" },
  includedFinish: { he: "גימור נקי ומושלם", ru: "Чистовая отделка", ar: "تشطيب نظيف ومثالي" },
  excludedMoisture: { he: "תיקוני רטיבות או איטום", ru: "Ремонт влажности / гидроизоляция", ar: "إصلاحات الرطوبة والعزل المائي" },
  excludedElectrical: { he: "עבודות חשמל נוספות", ru: "Доп. электрика", ar: "أعمال كهربائية إضافية" },

  // ── Customer form ──
  customerDetails: { he: "פרטי לקוח", ru: "Ваши данные", ar: "بيانات العميل" },
  fullName: { he: "שם מלא", ru: "Имя", ar: "الاسم الكامل" },
  phone: { he: "טלפון", ru: "Телефон", ar: "الهاتف" },
  city: { he: "עיר", ru: "Город", ar: "المدينة" },
  emailField: { he: "אימייל", ru: "Email", ar: "البريد الإلكتروني" },
  addressField: { he: "כתובת", ru: "Адрес", ar: "العنوان" },
  notesField: { he: "הערות", ru: "Примечания", ar: "ملاحظات" },
  additionalDetails: { he: "פרטים נוספים (אופציונלי)", ru: "Доп. информация (по желанию)", ar: "تفاصيل إضافية (اختياري)" },
  invalidPhone: { he: "מספר טלפון לא תקין — יש להזין מספר שמתחיל ב-05", ru: "Неверный номер — должен начинаться с 05", ar: "رقم هاتف غير صحيح — يجب أن يبدأ بـ 05" },
  invalidEmail: { he: "כתובת אימייל לא תקינה", ru: "Неверный email", ar: "البريد الإلكتروني غير صحيح" },
  sendEmail: { he: "שלחו לי הצעה מסודרת", ru: "Отправьте мне оформленное предложение", ar: "أرسلوا لي عرضًا منظمًا" },
  detailsConversionHint: { he: "השאירו פרטים ונחזור אליכם עם הצעה מסודרת לפי הבחירות שביצעתם", ru: "Оставьте данные — мы вернёмся с предложением по вашему выбору", ar: "اتركوا تفاصيلكم وسنعود إليكم بعرض منظم حسب اختياراتكم" },
  detailsTrustLine: { he: "ללא התחייבות • נחזור אליכם בהקדם", ru: "Без обязательств • Мы свяжемся с вами", ar: "بدون التزام • سنعود إليكم قريبًا" },
  downloadPdf: { he: "הורד PDF", ru: "Скачать PDF", ar: "تحميل PDF" },
  saveToPersonal: { he: "שמור באזור האישי", ru: "Сохранить в кабинете", ar: "حفظ في حسابي" },
  quoteSentSuccess: { he: "ההצעה נשלחה בהצלחה!", ru: "Отправлено!", ar: "تم الإرسال بنجاح!" },
  willContactSoon: { he: "ניצור איתך קשר בהקדם", ru: "Мы скоро свяжемся с вами", ar: "سنتواصل معك قريباً" },
  preparingQuote: { he: "מכין הצעת מחיר...", ru: "Готовим смету…", ar: "جارٍ تحضير العرض…" },
  pleaseWait: { he: "אנא המתן", ru: "Подождите", ar: "يرجى الانتظار" },
  savedSuccess: { he: "נשמר בהצלחה", ru: "Сохранено", ar: "تم الحفظ" },
  savedDesc: { he: "החישוב נשמר באזור האישי שלך", ru: "Расчёт сохранён в вашем кабинете", ar: "تم حفظ الحساب في حسابك" },
  saveError: { he: "שגיאה בשמירה", ru: "Ошибка сохранения", ar: "خطأ في الحفظ" },
  quoteError: { he: "שגיאה ביצירת הצעת מחיר", ru: "Ошибка при создании сметы", ar: "خطأ في إنشاء العرض" },

  // ── Auth ──
  login: { he: "התחברות", ru: "Войти", ar: "تسجيل الدخول" },
  logout: { he: "התנתקות", ru: "Выйти", ar: "تسجيل الخروج" },
  myCalculations: { he: "החישובים שלי", ru: "Мои расчёты", ar: "حساباتي" },
  myAccount: { he: "החשבון שלי", ru: "Мой профиль", ar: "حسابي" },
  authDefaultReason: { he: "התחבר כדי לשמור חישובים ולגשת אליהם בכל זמן", ru: "Войдите, чтобы сохранять расчёты", ar: "سجّل الدخول لحفظ حساباتك" },
  continueWithGoogle: { he: "המשך עם Google", ru: "Войти через Google", ar: "الدخول عبر Google" },
  continueWithApple: { he: "המשך עם Apple", ru: "Войти через Apple", ar: "الدخول عبر Apple" },
  connecting: { he: "מתחבר...", ru: "Вход…", ar: "جارٍ الدخول…" },
  authDisclaimer: { he: "ההתחברות מאפשרת שמירת חישובים בלבד. אין חובה להתחבר.", ru: "Вход нужен только для сохранения расчётов. Необязательно.", ar: "تسجيل الدخول فقط لحفظ الحسابات. غير إلزامي." },
  loginToSave: { he: "התחבר כדי לשמור את החישוב שלך", ru: "Войдите, чтобы сохранить расчёт", ar: "سجّل الدخول لحفظ الحساب" },

  // ── My Calculations ──
  myCalcTitle: { he: "החישובים שלי", ru: "Мои расчёты", ar: "حساباتي" },
  myCalcSubtitle: { he: "כל ההצעות והחישובים השמורים שלך", ru: "Все сохранённые расчёты", ar: "جميع الحسابات المحفوظة" },
  newCalc: { he: "חישוב חדש", ru: "Новый расчёт", ar: "حساب جديد" },
  loginToSeeCalc: { he: "התחבר כדי לראות את החישובים שלך", ru: "Войдите, чтобы увидеть расчёты", ar: "سجّل الدخول لرؤية حساباتك" },
  saveAndAccessAnywhere: { he: "שמור חישובים וגש אליהם מכל מכשיר", ru: "Сохраняйте и открывайте с любого устройства", ar: "احفظ وافتح من أي جهاز" },
  noSavedCalc: { he: "אין חישובים שמורים", ru: "Пока пусто", ar: "لا توجد حسابات محفوظة" },
  createNewCalcHint: { he: "צור חישוב חדש ושמור אותו כאן", ru: "Создайте расчёт и сохраните его здесь", ar: "أنشئ حساباً جديداً واحفظه هنا" },
  noTitle: { he: "ללא כותרת", ru: "Без названия", ar: "بدون عنوان" },
  openAndEdit: { he: "פתח ועריכה", ru: "Открыть", ar: "فتح وتعديل" },
  duplicate: { he: "שכפל", ru: "Копировать", ar: "نسخ" },
  deleteCalc: { he: "למחוק את החישוב?", ru: "Удалить расчёт?", ar: "حذف الحساب؟" },
  deleteCalcDesc: { he: "פעולה זו לא ניתנת לביטול.", ru: "Это нельзя отменить.", ar: "لا يمكن التراجع عن هذا." },
  deletedSuccess: { he: "נמחק בהצלחה", ru: "Удалено", ar: "تم الحذف" },
  deleteError: { he: "שגיאה במחיקה", ru: "Ошибка удаления", ar: "خطأ في الحذف" },
  duplicatedSuccess: { he: "שוכפל בהצלחה", ru: "Скопировано", ar: "تم النسخ" },
  duplicateError: { he: "שגיאה בשכפול", ru: "Ошибка копирования", ar: "خطأ في النسخ" },
  loadingError: { he: "שגיאה בטעינת החישובים", ru: "Ошибка загрузки", ar: "خطأ في التحميل" },

  // ── Custom addon form ──
  addAddon: { he: "הוסף תוספת", ru: "Добавить позицию", ar: "إضافة بند" },
  addonDescriptionLabel: { he: "תיאור *", ru: "Описание *", ar: "الوصف *" },
  addonDescriptionPlaceholder: { he: "תיאור התוספת", ru: "Что добавляем", ar: "وصف الإضافة" },
  addonDescriptionRequired: { he: "יש להזין תיאור", ru: "Введите описание", ar: "أدخل الوصف" },
  addonQuantityLabel: { he: "כמות *", ru: "Кол-во *", ar: "الكمية *" },
  addonUnitLabel: { he: "יחידה", ru: "Единица", ar: "الوحدة" },
  addonUnitPriceLabel: { he: "מחיר ליחידה *", ru: "Цена за шт. *", ar: "سعر الوحدة *" },
  addonPriceZeroWarning: { he: "מחיר ליחידה הוא 0", ru: "Цена за шт. = 0", ar: "سعر الوحدة = 0" },
  addonPriceZeroShort: { he: "מחיר 0", ru: "Цена 0", ar: "سعر 0" },
  addonUpdate: { he: "עדכן", ru: "Обновить", ar: "تحديث" },
  addonAdd: { he: "הוסף", ru: "Добавить", ar: "إضافة" },
  addonDescription: { he: "תיאור", ru: "Описание", ar: "الوصف" },
  addonQuantity: { he: "כמות", ru: "Кол-во", ar: "الكمية" },
  addonUnit: { he: "יח׳", ru: "шт.", ar: "قطعة" },
  addonUnitM: { he: "מ׳", ru: "м", ar: "م" },
  addonUnitSqm: { he: "מ״ר", ru: "м²", ar: "م²" },
  addonUnitPrice: { he: "מחיר ליחידה", ru: "Цена за шт.", ar: "سعر الوحدة" },
  addonSave: { he: "שמור", ru: "Сохранить", ar: "حفظ" },

  // ── Pricing line items ──
  priceCeiling: { he: "תקרה", ru: "Потолок", ar: "السقف" },
  priceExtraCorners: { he: "תוספת פינות", ru: "Доп. углы", ar: "زوايا إضافية" },
  priceMikvahFactor: { he: "מקדם מקווה", ru: "Коэф. миквы", ar: "معامل المكفه" },
  priceHeightSurcharge: { he: "תוספת גובה (מעל", ru: "Надбавка за высоту (свыше", ar: "إضافة ارتفاع (أكثر من" },
  priceLargeArea: { he: "תוספת שטח גדול (מעל", ru: "Надбавка за площадь (свыше", ar: "إضافة مساحة كبيرة (أكثر من" },
  priceSpotlights: { he: "ספוטים שקועים", ru: "Встроенные споты", ar: "سبوتات مدمجة" },
  priceRecessedLed: { he: "פס לד שקוע", ru: "Встроенная LED-лента", ar: "شريط LED مدمج" },
  pricePerimeterLight: { he: "תאורה היקפית עם ניתוק", ru: "Свет по периметру", ar: "إضاءة محيطية" },
  priceCurtainLight: { he: "תאורה נסתרת לוילון", ru: "Подсветка карниза", ar: "إضاءة مخفية للستارة" },
  priceSurfaceMount: { he: "תאורה צמודת תקרה", ru: "Накладной светильник", ar: "إضاءة سطحية" },
  priceMagneticTrack: { he: "פס מגנטי שקוע", ru: "Магнитный трек", ar: "مسار مغناطيسي" },
  priceMagneticHeads: { he: "גופי תאורה לפס מגנטי", ru: "Светильники для трека", ar: "وحدات إضاءة للمسار" },
  pricePendantLight: { he: "תאורה תלויה", ru: "Подвесной светильник", ar: "إضاءة معلّقة" },
  priceThermalInsulation: { he: "בידוד תרמי", ru: "Теплоизоляция", ar: "عزل حراري" },
  priceAcousticInsulation: { he: "בידוד אקוסטי", ru: "Шумоизоляция", ar: "عزل صوتي" },
  priceServiceHatch: { he: "פתח שירות 60x60", ru: "Люк 60×60", ar: "فتحة خدمة 60×60" },
  priceHiddenCurtain: { he: "נישה עבור וילון נסתר", ru: "Ниша под карниз", ar: "تجويف لستارة مخفية" },
  priceGypsumBulkhead: { he: "סינר גבס היקפי", ru: "Гипсовый короб", ar: "إطار جبس محيطي" },
  priceDismantleCeiling: { he: "פירוק תקרה קיימת", ru: "Демонтаж потолка", ar: "إزالة السقف القديم" },
  priceDismantleLightPoints: { he: "פירוק נקודות תאורה", ru: "Демонтаж светильников", ar: "إزالة نقاط الإضاءة" },

  // ── Stepper aria ──
  decrease: { he: "הפחת", ru: "Меньше", ar: "أقل" },
  increase: { he: "הוסף", ru: "Больше", ar: "أكثر" },

  // ── Finish labels for estimate page ──
  finishLabelGlossy: { he: "מבריק", ru: "Глянец", ar: "لامع" },
  finishLabelMatte: { he: "מט", ru: "Мат", ar: "مطفي" },
  finishLabelSatin: { he: "סאטן", ru: "Сатин", ar: "ساتان" },
  finishLabelBacklit: { he: "תקרה מוארת", ru: "Световой", ar: "سقف مضيء" },
  finishLabelBacklitPrint: { he: "מוארת עם הדפס", ru: "Световой с печатью", ar: "مضيء مع طباعة" },
  finishLabelPrint: { he: "הדפס", ru: "Печать", ar: "طباعة" },

  // ── Disclaimers for quote ──
  disclaimerEstimated: { he: "המחיר משוער בהתאם לנתונים שהוזנו. המחיר הסופי נקבע לאחר בדיקה/מדידה במידת הצורך.", ru: "Цена ориентировочная. Точная стоимость — после осмотра и замера.", ar: "السعر تقديري. السعر النهائي — بعد المعاينة والقياس." },
  disclaimerExcludes: { he: "לא כולל תיקוני רטיבות, איטום או עבודות חשמל נוספות.", ru: "Не включает: ремонт влажности, гидроизоляцию, доп. электрику.", ar: "لا يشمل: إصلاحات الرطوبة، العزل المائي، الأعمال الكهربائية الإضافية." },

  // ── Email ──
  emailGreeting: { he: "שלום, אשמח לקבל הצעת מחיר מספר", ru: "Здравствуйте, прошу выслать смету №", ar: "مرحباً، أرغب في الحصول على عرض سعر رقم" },

  // ── Placeholders ──
  placeholderFullName: { he: "ישראל ישראלי", ru: "Иван Иванов", ar: "محمد أحمد" },
  placeholderPhone: { he: "050-1234567", ru: "050-1234567", ar: "050-1234567" },
  placeholderCity: { he: "תל אביב", ru: "Тель-Авив", ar: "تل أبيب" },
  placeholderEmail: { he: "email@example.com", ru: "email@example.com", ar: "email@example.com" },
  placeholderAddress: { he: "רח' הרצל 1", ru: "ул. Герцля 1", ar: "شارع هرتسل 1" },
  placeholderNotes: { he: "הערות נוספות...", ru: "Доп. заметки…", ar: "ملاحظات إضافية…" },

  quoteSentTitle: { he: "ההצעה נשלחה בהצלחה", ru: "Отправлено!", ar: "تم الإرسال بنجاح" },
  quoteSentDesc: { he: "ניצור איתך קשר בהקדם", ru: "Мы скоро свяжемся с вами", ar: "سنتواصل معك قريباً" },
  sendingEmail: { he: "שולח את ההצעה...", ru: "Отправка предложения…", ar: "جارٍ إرسال العرض…" },
  emailSendError: { he: "שגיאה בשליחת האימייל — נסה שוב", ru: "Ошибка отправки — попробуйте снова", ar: "خطأ في الإرسال — حاول مرة أخرى" },
  emailRequired: { he: "יש להזין כתובת אימייל כדי לשלוח את ההצעה", ru: "Введите email для отправки предложения", ar: "أدخل البريد الإلكتروني لإرسال العرض" },

  // ── Callback checkbox ──
  callbackCheckbox: { he: "נא ליצור איתי קשר לייעוץ חינם", ru: "Свяжитесь со мной для бесплатной консультации", ar: "يرجى التواصل معي لاستشارة مجانية" },
  leadSaved: { he: "הפרטים נשמרו בהצלחה", ru: "Данные сохранены", ar: "تم حفظ البيانات" },
  leadSaveError: { he: "שגיאה בשמירת הפרטים", ru: "Ошибка сохранения", ar: "خطأ في حفظ البيانات" },

  // ── Admin leads ──
  adminLeads: { he: "לידים", ru: "Заявки", ar: "الطلبات" },
  adminSettings: { he: "הגדרות", ru: "Настройки", ar: "الإعدادات" },
  adminEmailLabel: { he: "אימייל לקבלת התראות", ru: "Email для уведомлений", ar: "بريد الإشعارات" },
  adminEmailPlaceholder: { he: "admin@example.com", ru: "admin@example.com", ar: "admin@example.com" },
  callbackRequested: { he: "ביקש חזרה", ru: "Просит перезвонить", ar: "طلب معاودة الاتصال" },
  statusNew: { he: "חדש", ru: "Новый", ar: "جديد" },
  statusContacted: { he: "נוצר קשר", ru: "Связались", ar: "تم الاتصال" },
  statusNoAnswer: { he: "לא ענה", ru: "Не ответил", ar: "لم يرد" },
  statusClosed: { he: "סגור", ru: "Закрыт", ar: "مغلق" },
  filterAll: { he: "הכל", ru: "Все", ar: "الكل" },
  filterCallbackOnly: { he: "ביקשו חזרה", ru: "Просят перезвонить", ar: "طلبوا اتصالاً" },
  searchLeads: { he: "חיפוש לפי שם או טלפון...", ru: "Поиск по имени или телефону…", ar: "بحث بالاسم أو الهاتف…" },
  noLeads: { he: "אין לידים עדיין", ru: "Заявок пока нет", ar: "لا توجد طلبات بعد" },
  leadDetails: { he: "פרטי ליד", ru: "Детали заявки", ar: "تفاصيل الطلب" },
  updateStatus: { he: "עדכן סטטוס", ru: "Обновить статус", ar: "تحديث الحالة" },

  // ── Warranty popup ──
  warrantyTitle: { he: "תעודת אחריות שניתנת לאחר ההתקנה", ru: "Гарантийный сертификат после монтажа", ar: "شهادة ضمان تُمنح بعد التركيب" },
  warrantyLink: { he: "תעודת אחריות שניתנת לאחר ההתקנה", ru: "Гарантийный сертификат (выдаётся после монтажа)", ar: "شهادة الضمان (تُمنح بعد التركيب)" },
  warrantyBody: { he: `Sky View – תקרות מעוצבות
skyview.co.il

אנו מעניקים אחריות על תקרה מתוחה שהותקנה על ידינו, בכפוף לתנאים הבאים:

• אחריות על יריעת התקרה – 10 שנים
• אחריות על ההתקנה – 12 חודשים
• אחריות על רכיבי תאורה וחשמל – בהתאם לאחריות היצרן בלבד, אלא אם צוין אחרת בכתב

האחריות חלה על פגמי ייצור מהותיים ביריעת התקרה ועל ליקויים שנגרמו ישירות כתוצאה מהתקנה שבוצעה על ידי Sky View.

האחריות תקפה רק לאחר סיום העבודה, אישור קבלת העבודה ותשלום מלא.

האחריות אינה חלה על:
נזק מכני, קרע, חור, שריטה, נזילה, הצפה, רטיבות, חדירת מים, כשל בתשתיות קיימות, בעיות במבנה, עבודות או שינויים שבוצעו לאחר ההתקנה, שימוש או ניקוי לא נכונים, וכן בלאי סביר.

במקרה של תקלה יש לפנות אלינו בהקדם, בצירוף תיאור התקלה ותמונות.
כל פנייה תיבדק באופן מקצועי, והטיפול יבוצע בהתאם לתנאי האחריות.
האחריות מוגבלת לתיקון או להחלפת הרכיב הפגום בלבד, לפי שיקול דעת החברה.

בברכה,
צוות Sky View`, ru: `Sky View — дизайнерские потолки
skyview.co.il

Мы предоставляем гарантию на натяжной потолок, установленный нашей командой, на следующих условиях:

• Гарантия на полотно — 10 лет
• Гарантия на монтаж — 12 месяцев
• Гарантия на светильники и электрику — в рамках гарантии производителя, если иное не указано письменно

Гарантия распространяется на существенные производственные дефекты полотна и на недостатки, возникшие непосредственно в результате монтажа компанией Sky View.

Гарантия вступает в силу после завершения работ, приёмки и полной оплаты.

Гарантия не покрывает:
Механические повреждения, разрывы, проколы, царапины, протечки, затопления, сырость, проникновение воды, неисправности существующих коммуникаций, проблемы с конструкцией здания, работы или изменения после монтажа, неправильное использование или чистку, а также естественный износ.

При обнаружении неисправности свяжитесь с нами, приложив описание и фото.
Каждое обращение будет рассмотрено профессионально, ремонт выполняется согласно условиям гарантии.
Гарантия ограничивается ремонтом или заменой дефектного элемента по усмотрению компании.

С уважением,
Команда Sky View`, ar: `Sky View — أسقف مصممة
skyview.co.il

نمنح ضماناً على السقف المشدود الذي تم تركيبه من قِبلنا، وفقاً للشروط التالية:

• ضمان على قماش السقف — 10 سنوات
• ضمان على التركيب — 12 شهراً
• ضمان على عناصر الإضاءة والكهرباء — حسب ضمان الشركة المصنّعة فقط، ما لم يُذكر غير ذلك كتابياً

يسري الضمان على عيوب التصنيع الجوهرية في قماش السقف وعلى الأعطال الناتجة مباشرة عن التركيب الذي قامت به Sky View.

يبدأ الضمان فقط بعد إتمام العمل والموافقة عليه والدفع الكامل.

لا يشمل الضمان:
الأضرار الميكانيكية، التمزق، الثقوب، الخدوش، التسريبات، الفيضانات، الرطوبة، تسرب المياه، أعطال البنية التحتية القائمة، مشاكل في المبنى، أعمال أو تعديلات بعد التركيب، الاستخدام أو التنظيف غير الصحيح، والتآكل الطبيعي.

في حال وجود عطل، يرجى التواصل معنا مع وصف المشكلة وصور.
كل طلب يُفحص بشكل مهني، والإصلاح يتم وفقاً لشروط الضمان.
الضمان يقتصر على إصلاح أو استبدال العنصر المعيب فقط، وفقاً لتقدير الشركة.

مع التحية،
فريق Sky View` },

  // ── Trust / conversion block (Step 4) ──
  trustBlockTitle: { he: "למה Sky View?", ru: "Почему Sky View?", ar: "لماذا Sky View؟" },
  trustAfterSubmit: { he: "לאחר שליחת הפרטים, נציג שלנו יחזור אליכם לתיאום מדידה ואישור סופי.", ru: "После отправки наш специалист свяжется с вами для замера и окончательного подтверждения.", ar: "بعد الإرسال، سيتواصل معكم مندوبنا لتنسيق القياس والتأكيد النهائي." },
  trustPriceNote: { he: "המחיר מבוסס על הבחירות שלך וייתכנו התאמות קלות לאחר בדיקה בשטח.", ru: "Цена рассчитана по вашему выбору и может незначительно измениться после осмотра на месте.", ar: "السعر مبني على اختياراتك وقد يتغيّر قليلاً بعد المعاينة في الموقع." },
  trustCleanInstall: { he: "התקנה נקייה ומקצועית", ru: "Чистый профессиональный монтаж", ar: "تركيب نظيف واحترافي" },
  trustFinishQuality: { he: "גימור מושלם", ru: "Безупречная отделка", ar: "تشطيب مثالي" },
  trustOrganizedService: { he: "שירות מסודר ואמין", ru: "Организованный и надёжный сервис", ar: "خدمة منظمة وموثوقة" },
  detailsTrust1: { he: "הצעה מסודרת לפי הבחירות שביצעתם", ru: "Предложение на основе ваших выборов", ar: "عرض منظم بناءً على اختياراتكم" },
  detailsTrust2: { he: "מענה מהיר ותיאום המשך", ru: "Быстрый ответ и координация", ar: "رد سريع وتنسيق متابعة" },
  detailsTrust3: { he: "המחיר הסופי ייקבע לאחר בדיקה בשטח", ru: "Окончательная цена после осмотра на месте", ar: "السعر النهائي يُحدد بعد المعاينة في الموقع" },
  summaryStrip: { he: "סיכום הפרויקט שלך", ru: "Ваш проект", ar: "ملخص مشروعك" },
  showOnlyVizColors: { he: "הצג רק צבעים עם הדמיה", ru: "Только цвета с визуализацией", ar: "عرض الألوان مع معاينة فقط" },
  colorNoVizNote: {
    he: "* לצבע שנבחר אין כרגע הדמיה זמינה. הבחירה נשמרת להצעת המחיר, אך התמונה המוצגת היא להמחשה בלבד.",
    ru: "* Для выбранного цвета визуализация пока недоступна. Выбор сохранён для расчёта, но изображение показано для примера.",
    ar: "* لا تتوفر حالياً معاينة للون المختار. تم حفظ الاختيار لعرض السعر، لكن الصورة المعروضة للتوضيح فقط.",
  },
  mainLightingLabel: { he: "תאורה עיקרית", ru: "Основное освещение", ar: "الإضاءة الرئيسية" },
  noLighting: { he: "ללא", ru: "Нет", ar: "بدون" },
  pricingBreakdownSection: { he: "פירוט מחיר", ru: "Детализация цены", ar: "تفاصيل السعر" },
  extrasAndAddonsSection: { he: "תוספות ופרטים נוספים", ru: "Дополнения и детали", ar: "إضافات وتفاصيل أخرى" },
  includedExcludedSection: { he: "מה כלול / לא כלול", ru: "Что входит / не входит", ar: "ما يشمله / لا يشمله السعر" },
} as const;

export type TranslationKey = keyof typeof translations;

export function t(key: TranslationKey, lang: Language): string {
  const entry = translations[key];
  if (!entry) return key;
  return (entry as any)[lang] || (entry as any).he;
}

// ── Context ──

export interface LanguageContextType {
  lang: Language;
  dir: "rtl" | "ltr";
  setLang: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

export const LanguageContext = createContext<LanguageContextType>({
  lang: "he",
  dir: "rtl",
  setLang: () => {},
  t: (key) => t(key, "he"),
});
